/* @jsxImportSource @emotion/react */
import React from 'react'
import { css } from '@emotion/react'
import classNames from 'classnames'

// Grain tile parameterized by dot color + base fill, so we can stamp the same
// noise texture in different brand tints for the optional bottom inlay.
const grainTileSvg = (dotColor: string, baseColor: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.9 0.9" stitchTiles="stitch" numOctaves="3" seed="8345" result="noise"/><feColorMatrix in="noise" type="luminanceToAlpha" result="alphaNoise"/><feComponentTransfer in="alphaNoise" result="discreteNoise"><feFuncA type="discrete" tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0"/></feComponentTransfer><feFlood flood-color="${dotColor}" result="dotColor"/><feComposite in="dotColor" in2="discreteNoise" operator="in" result="noiseDots"/><feMerge><feMergeNode in="SourceGraphic"/><feMergeNode in="noiseDots"/></feMerge></filter><rect width="100%" height="100%" fill="${baseColor}" filter="url(#n)"/></svg>`

const grainUri = (dotColor: string, baseColor: string) =>
  `url("data:image/svg+xml;utf8,${encodeURIComponent(grainTileSvg(dotColor, baseColor))}")`

// Default (unmodified) grain — warm beige dots on near-white.
const GRAIN_URI = grainUri('#f9efe4', '#fdf9f4')

// Brand-tinted grain tiles for the bottom inlay. Colors echo the security
// section illustrations: orange / blue / pink.
const INLAY_URIS: Record<ColoredInlay, string> = {
  orange: grainUri('#FFC5A0', '#FFD8C0'),
  blue: grainUri('#B5DEFC', '#D5EEFE'),
  pink: grainUri('#FFCFE0', '#FFE0EA'),
}

type ColoredInlay = 'orange' | 'blue' | 'pink'

const baseGrainCardCSS = css`
  position: relative;
  overflow: hidden;
  isolation: isolate;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: ${GRAIN_URI};
    background-size: 200px 200px;
    background-repeat: repeat;
    pointer-events: none;
    z-index: -1;
  }
`

// Inlay sits on top of the base grain in the bottom third — hard top edge.
// Same `z-index: -1` keeps children untouched.
const inlayCss = (variant: ColoredInlay) => css`
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 33.333%;
    background-image: ${INLAY_URIS[variant]};
    background-size: 200px 200px;
    background-repeat: repeat;
    pointer-events: none;
    z-index: -1;
  }
`

type GrainCardProps = React.ComponentProps<'div'> & {
  coloredInlay?: ColoredInlay
}

export const GrainCard = ({
  children,
  className,
  style,
  coloredInlay,
  ...props
}: GrainCardProps) => {
  return (
    <div
      {...props}
      css={
        coloredInlay
          ? [baseGrainCardCSS, inlayCss(coloredInlay)]
          : baseGrainCardCSS
      }
      style={{ borderRadius: 32, ...style }}
      className={classNames('background-moon-dust-lighter', className)}
    >
      {children}
    </div>
  )
}
